import React from 'react';
import cn from 'classnames';

import { MessageType } from 'client/redux/pubnub/types';
import { selectRoomId } from 'client/redux/room/selectors';
import { PB_CHANNELS } from 'client/redux/pubnub/constants';
import { useAppDispatch, useAppSelector, useTranslation, useGAEventCommonProps } from 'client/hooks';
import {
  addListenerToAllowToSpeakList,
  deleteMessage,
  publishMessage,
  removeListenerFromAllowToSpeakList,
  setUserMetadata,
  updateRaiseHandMessage,
  Response as SetUserMetadataResponse,
} from 'client/redux/pubnub/actions';
import { selectListenersAllowedToSpeak } from 'client/redux/pubnub/selectors';
import { updatePermissions } from 'client/redux/mux-details/actions';
import { setListenerHistory } from 'client/redux/room/actions';
import { selectRoomDbData } from 'client/redux/room/selectors';
import { RaiseHandMessage } from 'client/redux/pubnub/types';
import { analytics } from 'client/utils/analytics';

import Icon from '../Icon';

import css from './ConnectionControls.module.scss';

type Props = {
  listener: RaiseHandMessage;
  isAllowedToSpeak?: boolean;
  onSelectAction?: () => void;
  className?: string;
};

const ConnectionControls = React.memo((props: Props) => {
  const roomId = useAppSelector(selectRoomId);
  const dispatch = useAppDispatch();
  const { common } = useGAEventCommonProps();
  const [isClicked, setIsClicked] = React.useState(false);
  const { isAllowedToSpeak = false, listener, onSelectAction, className = '' } = props;
  const roomData = useAppSelector(selectRoomDbData);
  const { translate } = useTranslation('connectionsControll');
  const listOfListenersAllowedToSpeak = useAppSelector(selectListenersAllowedToSpeak);
  const listenerAllowedToSpeak = listOfListenersAllowedToSpeak && listOfListenersAllowedToSpeak[0];

  const onBlockClick = React.useCallback(async () => {
    console.info('Block user = ', listener.id);
    setIsClicked(true);

    if (isAllowedToSpeak) {
      const res = await dispatch(removeListenerFromAllowToSpeakList({ roomId, listenerId: listener.id }));

      if (res.meta.requestStatus === 'fulfilled')
        dispatch(
          updatePermissions({ userId: listener.id, roomId, permissions: { canPublish: false, canSubscribe: true } }),
        );
    }
    // Set listener blocked status in history
    dispatch(setListenerHistory({ uid: listener.id, roomId: roomId, isBlocked: true }));

    // send message to block list
    const result = await dispatch(
      publishMessage({
        channel: `${roomId}.${PB_CHANNELS.BLOCKED}`,
        message: JSON.stringify({ type: MessageType.BLOCK, id: listener.id, name: listener.name }),
      }),
    );

    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate('iconLabels.block'),
    });

    if (result.meta.requestStatus === 'fulfilled' && result.payload) {
      const data = result.payload as SetUserMetadataResponse;

      // update user metadata
      dispatch(
        setUserMetadata({
          uuid: `${roomId}.${PB_CHANNELS.USERS}.${listener.id}`,
          data: { externalId: data.timeToken },
        }),
      );
    }

    dispatch(
      deleteMessage({
        channel: roomId,
        timeToken: listener.messageTimeToken as string,
      }),
    );
    onSelectAction?.();
  }, [onSelectAction, roomId, dispatch, listener.id, isAllowedToSpeak, listener.name, listener.messageTimeToken]);

  const onPauseClick = React.useCallback(async () => {
    console.info('Move speaking user to waiting list = ', listener.id);
    setIsClicked(true);

    const res = await dispatch(removeListenerFromAllowToSpeakList({ roomId, listenerId: listener.id }));

    if (res.meta.requestStatus === 'fulfilled')
      dispatch(
        updatePermissions({ userId: listener.id, roomId, permissions: { canPublish: false, canSubscribe: true } }),
      );

    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate('iconLabels.pause'),
    });

    onSelectAction?.();
  }, [dispatch, listener.id, onSelectAction, roomId]);

  const onMuteClick = async () => {
    console.info('Move speaking user to other users = ', listener.id);

    const res = await dispatch(removeListenerFromAllowToSpeakList({ roomId, listenerId: listener.id }));

    if (res.meta.requestStatus === 'fulfilled')
      dispatch(
        updatePermissions({ userId: listener.id, roomId, permissions: { canPublish: false, canSubscribe: true } }),
      );

    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate('iconLabels.mute'),
    });

    dispatch(
      deleteMessage({
        channel: roomId,
        timeToken: listener.messageTimeToken as string,
      }),
    );
    onSelectAction?.();
    setIsClicked(true);
  };

  const onMicClick = React.useCallback(async () => {
    console.info('Admit or reconnect user to broadcast = ', listener.id);
    let removeRes = { meta: { requestStatus: 'fulfilled' } };
    setIsClicked(true);

    const addRes = await dispatch(
      addListenerToAllowToSpeakList({
        roomId,
        listenerId: listener.id,
        name: listener.name,
        messageTimeToken: `${listener.messageTimeToken}`,
      }),
    );

    // Set listener start time conversation in history (when admin or host will add listener to allow to speak list)
    if (roomData?.date) {
      const dateOfStart = new Date(roomData?.date);
      dispatch(
        setListenerHistory({
          uid: listener.id,
          roomId: roomId,
          conversations: [Date.now() - dateOfStart.valueOf()],
        }),
      );
    }

    // update user metadata about how many times he already speak
    await dispatch(
      setUserMetadata({
        uuid: `${roomId}.${PB_CHANNELS.USERS}.${listener.id}`,
        data: { email: `${Number(listener.alreadySpeakTotal || '0') + 1}`, name: listener.name },
      }),
    );

    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate('iconLabels.openMic'),
    });

    // if we have active listener (speaking) we remove it from allowed to speak list
    if (listenerAllowedToSpeak) {
      removeRes = await dispatch(removeListenerFromAllowToSpeakList({ roomId, listenerId: listenerAllowedToSpeak.id }));
      dispatch(
        deleteMessage({
          channel: roomId,
          timeToken: listenerAllowedToSpeak.messageTimeToken as string,
        }),
      );

      if (removeRes.meta.requestStatus === 'fulfilled') {
        await dispatch(
          updatePermissions({ userId: listener.id, roomId, permissions: { canPublish: false, canSubscribe: true } }),
        );
      }
    }

    if (addRes.meta.requestStatus === 'fulfilled' && removeRes.meta.requestStatus === 'fulfilled') {
      dispatch(
        updatePermissions({ userId: listener.id, roomId, permissions: { canPublish: true, canSubscribe: true } }),
      );
    }

    // update raise hand message action field
    dispatch(updateRaiseHandMessage({ messageTimetoken: `${listener.messageTimeToken}`, roomId }));

    onSelectAction?.();
  }, [
    roomId,
    listener.id,
    listener.name,
    listener.messageTimeToken,
    listener.alreadySpeakTotal,
    dispatch,
    onSelectAction,
    roomData?.date,
  ]);

  return (
    <div className={cn(className, css.connectionControls)}>
      {isAllowedToSpeak ? (
        <>
          <button
            type="button"
            title={translate('deletePermissionSpeak')}
            disabled={isClicked}
            className={css.iconWrap}
            onClick={onPauseClick}
          >
            <div className={css.icon}>
              <Icon type="pause" />
            </div>
            <span>{translate('iconLabels.pause')}</span>
          </button>
          <button
            type="button"
            title={translate('stopSpeaking')}
            disabled={isClicked}
            className={css.iconWrap}
            onClick={onMuteClick}
          >
            <div className={css.icon}>
              <Icon type="forbidden_microphone" />
            </div>
            <span>{translate('iconLabels.mute')}</span>
          </button>
          <button
            type="button"
            title={translate('blockUser')}
            disabled={isClicked}
            className={cn(css.iconWrap, css.block)}
            onClick={onBlockClick}
          >
            <div className={css.icon}>
              <Icon type="black_forbid" />
            </div>
            <span>{translate('iconLabels.block')}</span>
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            title={translate('addPermissionSpeak')}
            disabled={isClicked}
            className={css.iconWrap}
            onClick={onMicClick}
          >
            <div className={css.icon}>
              <Icon type="red_microphone" />
            </div>
            <span>{translate('iconLabels.openMic')}</span>
          </button>

          <button
            type="button"
            title={translate('blockUser')}
            disabled={isClicked}
            className={cn(css.iconWrap, css.block)}
            onClick={onBlockClick}
          >
            <div className={css.icon}>
              <Icon type="black_forbid" />
            </div>
            <span>{translate('iconLabels.block')}</span>
          </button>
        </>
      )}
    </div>
  );
});

ConnectionControls.displayName = 'ConnectionControls';

export default ConnectionControls;
